/* eslint-disable react/prop-types */
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Button,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import { HelpOutline } from "@mui/icons-material";

/**
 * OrganizationHeader Component
 *
 * A compact, enterprise-level header for organization management pages.
 * Features gradient background, icon, title, subtitle, optional count chip, and action buttons.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.icon - Icon component to display
 * @param {string} props.title - Main title text
 * @param {string} props.subtitle - Subtitle/description text
 * @param {number} props.count - Optional count to display in a chip (e.g., number of items)
 * @param {Function} props.onHelpClick - Optional help button click handler
 * @param {string} props.helpTooltip - Optional help button tooltip text
 * @param {string} props.helpAriaLabel - Optional help button aria label
 * @param {Array} props.actions - Optional array of action buttons [{label, icon, onClick, ariaLabel}]
 */
function OrganizationHeader({
  icon: Icon,
  title,
  subtitle,
  count,
  onHelpClick,
  helpTooltip,
  helpAriaLabel,
  actions,
}) {
  const theme = useTheme();

  return (
    <Box
      sx={{
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
        color: theme.palette.primary.contrastText,
        py: { xs: 2, sm: 2.5 },
        px: { xs: 2, sm: 3 },
        mb: 3,
        borderRadius: 2,
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Background Pattern */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          right: 0,
          width: "150px",
          height: "150px",
          background: `radial-gradient(circle, ${alpha(
            theme.palette.primary.contrastText,
            0.08
          )} 0%, transparent 70%)`,
          borderRadius: "50%",
          transform: "translate(50%, -50%)",
        }}
      />

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          position: "relative",
          zIndex: 1,
        }}
      >
        {/* Left Side: Icon, Title, Subtitle */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flex: 1 }}>
          {/* Icon */}
          {Icon && (
            <Box sx={{ fontSize: { xs: 20, sm: 24 } }}>
              <Icon sx={{ fontSize: "inherit" }} />
            </Box>
          )}

          {/* Title and Subtitle */}
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  letterSpacing: "-0.01em",
                  fontSize: { xs: "1.25rem", sm: "1.5rem" },
                }}
              >
                {title}
              </Typography>

              {/* Count Chip */}
              {count !== undefined && count !== null && (
                <Chip
                  label={count}
                  size="small"
                  sx={{
                    height: 24,
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    bgcolor: alpha(theme.palette.primary.contrastText, 0.2),
                    color: theme.palette.primary.contrastText,
                    backdropFilter: "blur(10px)",
                    border: `1px solid ${alpha(
                      theme.palette.primary.contrastText,
                      0.3
                    )}`,
                  }}
                />
              )}

              {/* Help Button */}
              {onHelpClick && (
                <Tooltip title={helpTooltip || ""}>
                  <IconButton
                    size="small"
                    onClick={onHelpClick}
                    aria-label={helpAriaLabel}
                    sx={{
                      color: theme.palette.primary.contrastText,
                      bgcolor: alpha(theme.palette.primary.contrastText, 0.15),
                      border: `1px solid ${alpha(
                        theme.palette.primary.contrastText,
                        0.2
                      )}`,
                      "&:hover": {
                        bgcolor: alpha(
                          theme.palette.primary.contrastText,
                          0.25
                        ),
                        transform: "translateY(-1px)",
                      },
                      transition: "all 0.2s ease",
                    }}
                  >
                    <HelpOutline sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>

            <Typography
              variant="body2"
              sx={{
                opacity: 0.9,
                fontWeight: 400,
                fontSize: { xs: "0.875rem", sm: "0.875rem" },
                mt: 0.5,
              }}
            >
              {subtitle}
            </Typography>
          </Box>
        </Box>

        {/* Right Side: Action Buttons */}
        {actions && actions.length > 0 && (
          <Box sx={{ display: "flex", gap: 1 }}>
            {actions.map((action, index) => (
              <Button
                key={index}
                variant="contained"
                size="medium"
                onClick={action.onClick}
                startIcon={action.icon}
                aria-label={action.ariaLabel}
                sx={{
                  bgcolor: alpha(theme.palette.primary.contrastText, 0.15),
                  color: theme.palette.primary.contrastText,
                  backdropFilter: "blur(10px)",
                  border: `1px solid ${alpha(
                    theme.palette.primary.contrastText,
                    0.2
                  )}`,
                  px: 3,
                  py: 1,
                  borderRadius: 2,
                  fontWeight: 600,
                  textTransform: "none",
                  "&:hover": {
                    bgcolor: alpha(theme.palette.primary.contrastText, 0.25),
                    transform: "translateY(-1px)",
                  },
                  transition: "all 0.2s ease",
                }}
              >
                {action.label}
              </Button>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default OrganizationHeader;
